import { useRef, useEffect, useState, memo, useLayoutEffect } from 'react';
import loadable from '@loadable/component';

import type { ReactNode } from 'react';

const Tooltip = loadable(() => import('@/components/Tooltip'));

import { useTheme } from '@simplywallst/ui-core';

import useWindowSize from '@/hooks/useWindowSize';
import { useServerEnv } from '@/hooks/useServerEnv';

import { VisxText } from '@/components/VisxText';

export interface Props {
  bound: number;
  titleColor?: string;
  valueColor?: string;
  typography?: keyof ReturnType<typeof useTheme>['typography'];
  negOrNull: boolean;
  onUpdate: (offset: number) => void;
  title: string;
  formattedValue: string;
  tooltip?: ReactNode;
}

interface State {
  titleYPos: number;
  titleOnTop: boolean;
  labelHeight: number;
}

const Label = ({
  bound,
  titleColor,
  valueColor,
  typography,
  negOrNull,
  onUpdate,
  title,
  formattedValue,
  tooltip,
}: Props) => {
  const titlePadding = 8;
  const titlePaddingBottom = 12;

  const serverEnv = useServerEnv();

  const titleRef = useRef<SVGSVGElement>(null);
  const valueRef = useRef<SVGSVGElement>(null);
  const barRef = useRef<SVGRectElement>(null);
  const hitAreaRef = useRef<SVGRectElement>(null);

  const size = useWindowSize();
  const [textWidth, setTextWidth] = useState(serverEnv.isServer ? 128 : 1);
  const [state, setState] = useState<State>({
    titleYPos: 0,
    titleOnTop: false,
    labelHeight: serverEnv.isServer ? 32 : 0,
  });

  const theme = useTheme();
  useEffect(() => {
    const { current: svg } = barRef;
    if (svg === null) return;
    setTextWidth(svg.getBBox().width);
  }, [textWidth, size]);

  useLayoutEffect(() => {
    const { current: title } = titleRef;
    const { current: value } = valueRef;
    const { current: hitArea } = hitAreaRef;
    const titleHeight = (title !== null && title.getBBox().height) || 0;
    const valueHeight = (value !== null && value.getBBox().height) || 0;
    //  Should the title sit on top of the value?
    const titleOnTop = titleHeight + titlePaddingBottom > Math.abs(bound);
    //  We need to work out if the chart range should be offset, so we calculate the label height from the top of the bar
    let offset = 0;
    let labelHeight = 0;
    //  If the title is on top of the bar
    if (titleOnTop) {
      //  The labelHeight is....
      labelHeight = titleHeight + valueHeight + titlePadding * 1.25;
    } else {
      //  If only the value is on top of the bar the labelHeight is....
      labelHeight = valueHeight + titlePadding;
    }
    //  Calc offset
    offset = 0 - labelHeight;
    //  If the offset is negative, the label wont be fully in view on the chart. So we need to offset the yScale range
    if (offset < 0) onUpdate(Math.round(Math.abs(offset)));
    //
    if (hitArea) {
      // We do this as the Bar's dimensions are re-calculated post render.
      // However the Tooltip will only clone the pre-rendered dimensions and
      // the hit area will not be re-rendered with updated props.
      // So we go through the back-door.
      hitArea.setAttribute('y', `${0 - labelHeight}`);
      hitArea.setAttribute('height', `${bound + labelHeight}`);
    }
    //
    setState({
      titleYPos: titleOnTop
        ? -(valueHeight + titlePadding * 1.25)
        : titlePadding,
      titleOnTop,
      labelHeight,
    });
  }, [bound, onUpdate, textWidth]);

  const { titleYPos, titleOnTop, labelHeight } = state;
  const ssrTitleOnTop = 17 + titlePaddingBottom > Math.abs(bound);
  const ssrTitleYPos = ssrTitleOnTop ? -34 : 10;
  const resolvedTitleYPos = serverEnv.isServer ? ssrTitleYPos : titleYPos;
  const resolvedTitleOnTop = serverEnv.isServer ? ssrTitleOnTop : titleOnTop;

  return (
    <>
      <rect
        x={0}
        y={0 - labelHeight}
        width="100%"
        height={labelHeight}
        ref={barRef}
        fill="transparent"
      />
      {titleColor && (
        <VisxText
          aria-label={title}
          innerRef={titleRef}
          y={0}
          typography={typography || 'xSmallStrong'}
          dx={titlePadding}
          dy={resolvedTitleYPos}
          lineHeight="1.4em"
          fill={
            resolvedTitleOnTop || typeof titleColor === 'undefined'
              ? theme.color.white
              : titleColor
          }
          width={textWidth - titlePadding * 6}
          verticalAnchor={resolvedTitleOnTop ? 'end' : 'start'}
          data-cy-id={`bar-chart-title-${title}`}
        >
          {title}
        </VisxText>
      )}

      <VisxText
        innerRef={valueRef}
        aria-label={formattedValue}
        typography={typography || 'largeStrong'}
        y={0}
        dx={titlePadding}
        dy={-titlePadding}
        lineHeight="1.4em"
        width={textWidth - titlePadding * 6}
        fill={
          negOrNull
            ? theme.color.bad
            : typeof valueColor === 'undefined'
              ? theme.color.white
              : valueColor
        }
        verticalAnchor="end"
        data-cy-id={`bar-chart-value-${title}`}
      >
        {formattedValue}
      </VisxText>

      {tooltip && (
        <Tooltip title={tooltip} placement="top-start">
          <rect
            ref={hitAreaRef}
            x="0"
            y={0 - labelHeight}
            width="100%"
            height={bound + labelHeight}
            fill="transparent"
          />
        </Tooltip>
      )}
    </>
  );
};

export default memo(Label);
